import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getTicketThread,
  replyAsAdmin,
  setTicketStatus,
} from "@/lib/support.functions";

export const Route = createFileRoute("/_authed/admin/support_/$ticketId")({
  head: () => ({
    meta: [
      { title: "Admin: Ticket: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminTicketPage,
});

function fmt(iso: string): string {
  return new Date(iso).toLocaleString();
}

type Status = "open" | "in_progress" | "resolved" | "closed";

function AdminTicketPage() {
  const { ticketId } = Route.useParams();
  const qc = useQueryClient();
  const threadFn = useServerFn(getTicketThread);
  const replyFn = useServerFn(replyAsAdmin);
  const statusFn = useServerFn(setTicketStatus);

  const [reply, setReply] = React.useState("");
  const [confirmStatus, setConfirmStatus] = React.useState<Status | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-support-thread", ticketId],
    queryFn: () => threadFn({ data: { ticketId } }),
  });

  const replyMut = useMutation({
    mutationFn: () => replyFn({ data: { ticketId, message: reply.trim() } }),
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["admin-support-thread", ticketId] });
      toast.success("Reply sent");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Failed to reply"),
  });

  const statusMut = useMutation({
    mutationFn: (s: Status) => statusFn({ data: { ticketId, status: s } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-support-thread", ticketId] });
      toast.success("Status updated");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Failed to update status"),
  });

  if (isLoading) return <div className="mx-auto max-w-3xl px-4 py-8">Loading…</div>;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-destructive">Ticket not found.</p>
        <Link to="/admin/support" className="underline">Back</Link>
      </div>
    );
  }

  const { ticket, messages } = data;

  function handleStatusClick(s: Status) {
    if (s === "in_progress") {
      statusMut.mutate(s);
    } else {
      setConfirmStatus(s);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-2">
        <Link to="/admin/support" className="text-xs text-brand-muted underline">
          ← All tickets
        </Link>
      </div>
      <div className="mb-6 border-b border-brand-line pb-4">
        <h1 className="font-serif text-2xl text-brand-ink">{ticket.subject}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-brand-muted">
          <Badge variant="outline">{ticket.user_email}</Badge>
          {ticket.category ? (
            <Badge variant="outline" className="capitalize">{ticket.category}</Badge>
          ) : null}
          <Badge variant="outline" className="capitalize">
            Status: {ticket.status.replace("_", " ")}
          </Badge>
          <span>Opened {fmt(ticket.created_at)}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={ticket.status === "in_progress" || statusMut.isPending}
            onClick={() => handleStatusClick("in_progress")}
          >
            Mark in progress
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={ticket.status === "resolved" || statusMut.isPending}
            onClick={() => handleStatusClick("resolved")}
          >
            Mark resolved
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={ticket.status === "closed" || statusMut.isPending}
            onClick={() => handleStatusClick("closed")}
          >
            Mark closed
          </Button>
        </div>
      </div>

      <ul className="space-y-4">
        {messages.map((m) => {
          const isAdmin = m.author_role === "admin";
          return (
            <li
              key={m.id}
              className={
                isAdmin
                  ? "rounded-lg border border-brand-accent/40 bg-brand-accent/10 p-4"
                  : "rounded-lg border border-brand-line bg-brand-paper p-4"
              }
            >
              <div className="mb-1 flex items-center justify-between text-xs text-brand-muted">
                <span className="font-medium uppercase tracking-wider">
                  {isAdmin ? "Admin" : m.author_email}
                </span>
                <span>{fmt(m.created_at)}</span>
              </div>
              <div className="whitespace-pre-wrap text-[14px] text-brand-ink">{m.body}</div>
            </li>
          );
        })}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (reply.trim().length > 0 && !replyMut.isPending) replyMut.mutate();
        }}
        className="mt-6 space-y-3"
      >
        <label className="block text-xs font-medium uppercase tracking-wider text-brand-muted">
          Admin reply
        </label>
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={5}
          maxLength={10000}
          placeholder="Reply to the user. This will be sent by email."
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={reply.trim().length === 0 || replyMut.isPending}>
            {replyMut.isPending ? "Sending…" : "Send reply"}
          </Button>
        </div>
      </form>

      <AlertDialog
        open={confirmStatus !== null}
        onOpenChange={(open) => { if (!open) setConfirmStatus(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Mark ticket as {confirmStatus}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmStatus === "resolved"
                ? "The user will receive a resolution email and can still reply if needed."
                : "The ticket will be closed. The user can open a new ticket if needed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmStatus) {
                  statusMut.mutate(confirmStatus);
                  setConfirmStatus(null);
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}